import type { cookies } from "next/headers";
import type { NextRequest } from "next/server";
import { getClientCookie } from "./cookies";

type CookieStore = Awaited<ReturnType<typeof cookies>>;

const GUEST_DETAILS_COOKIE_NAME = "guestDetails";

interface GuestDetails {
  emailAddress: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  agreedToTerms: boolean;
}

function isGuestDetails(obj: unknown): obj is GuestDetails {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "emailAddress" in obj &&
    typeof obj.emailAddress === "string" &&
    "firstName" in obj &&
    typeof obj.firstName === "string" &&
    "lastName" in obj &&
    typeof obj.lastName === "string" &&
    "phoneNumber" in obj &&
    typeof obj.phoneNumber === "string" &&
    "agreedToTerms" in obj &&
    typeof obj.agreedToTerms === "boolean"
  );
}

function parseGuestDetails(cookieValue: string): GuestDetails | undefined {
  try {
    const guestDetails = JSON.parse(cookieValue);
    return isGuestDetails(guestDetails) ? guestDetails : undefined;
  } catch {
    return undefined;
  }
}

export function getGuestDetailsClient(): GuestDetails | undefined {
  const cookieValue = getClientCookie(GUEST_DETAILS_COOKIE_NAME);
  return cookieValue ? parseGuestDetails(cookieValue) : undefined;
}

export function getGuestDetailsServer(
  request: NextRequest,
): GuestDetails | undefined {
  const cookieValue = request.cookies.get(GUEST_DETAILS_COOKIE_NAME)?.value;
  return cookieValue ? parseGuestDetails(cookieValue) : undefined;
}

export function setGuestDetailsServer(
  guestDetails: GuestDetails,
  cookieStore: CookieStore,
) {
  cookieStore.set(GUEST_DETAILS_COOKIE_NAME, JSON.stringify(guestDetails), {
    path: "/",
  });
}

export function deleteGuestDetailsServer(cookieStore: CookieStore) {
  cookieStore.delete(GUEST_DETAILS_COOKIE_NAME);
}
